/**
 * Extractors, as a place of their own.
 *
 * These are the agents that turn source rows into candidate records — not the
 * conversational agents under `/agents`, which are a model pointed at a
 * knowledge graph. Two different things that were both called "agents"; this
 * one keeps the workspace tab and answers to `/extractors`.
 *
 * An extractor belongs to exactly one graph — it is scoped to that model's node
 * types and nothing else — so this screen is the workspace's Agents tab with a
 * graph picker in front of it, rather than a second, subtly different list.
 * Which graph you are looking at lives in the query string, so the view is a
 * link like every other.
 */

import { useEffect } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { EmptyState, ErrorNotice, Loading } from '@/shared/components'
import { AgentsTab } from './AgentsTab'
import { GraphSwitcher } from '../shell/GraphSwitcher'
import { useGraphModel, useGraphs } from '../hooks'
import '../styles/brain.css'

export function ExtractorsPage() {
  const [params, setParams] = useSearchParams()
  const navigate = useNavigate()
  const graphs = useGraphs()

  const requested = params.get('graph')
  const graphId = requested ?? graphs.data?.[0]?.graph_id
  const model = useGraphModel(graphId)

  // A graph named in the link that no longer exists should fall back to the
  // first one rather than leaving the page stuck on an error.
  const stale = Boolean(
    requested && graphs.data && !graphs.data.some((g) => g.graph_id === requested),
  )
  useEffect(() => {
    if (stale) setParams({}, { replace: true })
  }, [stale, setParams])

  function switchTo(id: string) {
    setParams({ graph: id }, { replace: true })
  }

  if (graphs.isLoading) return <Loading label="Loading extractors…" />
  if (graphs.error) {
    return (
      <div className="page">
        <ErrorNotice error={graphs.error} onRetry={graphs.refetch} />
      </div>
    )
  }

  if (!graphs.data?.length) {
    return (
      <div className="page">
        <EmptyState
          title="No graphs yet"
          action={
            <button className="btn btn-primary" onClick={() => navigate('/brain')}>
              Create a graph
            </button>
          }
        >
          An agent is scoped to one graph's model — it can only report the node types and
          relationships that graph defines. Make a graph first and its agents appear here.
        </EmptyState>
      </div>
    )
  }

  return (
    <div className="brain">
      <div className="brain-workspace">
        <div className="cp-topbar">
          <div className="cp-crumb">
            <span className="hide-narrow">Extractors</span>
            <span className="faint hide-narrow">/</span>
            <GraphSwitcher graphs={graphs.data} graphId={graphId ?? ''} onSwitch={switchTo} />
          </div>
          <span className="cp-spacer" />
          {graphId && (
            <Link className="btn btn-sm" to={`/brain/${graphId}?tab=agents`}>
              Open the workspace →
            </Link>
          )}
        </div>

        {model.isLoading && <Loading label="Loading the model…" />}
        {model.error && (
          <div style={{ padding: 16 }}>
            <ErrorNotice error={model.error} onRetry={model.refetch} />
          </div>
        )}
        {graphId && model.data && <AgentsTab graphId={graphId} model={model.data} />}
      </div>
    </div>
  )
}
